import { ArrowLeftOutlined, CheckOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Flex, Input } from 'antd';
import React, { useRef, useState } from 'react';
import { useDeleteTemplate, useUpsertTemplate } from '~/hooks/useConversations';

function TemplateEditor({ activeMerchantId, template, onBack }) {
  const [content, setContent] = useState(template.content);
  const [showSuccess, setShowSuccess] = useState(false);
  const timerRef = useRef(null);
  const btnRef = useRef(null);
  const btnWidthRef = useRef(undefined);
  const queryClient = useQueryClient();
  const upsertMutation = useUpsertTemplate(activeMerchantId, queryClient);
  const deleteMutation = useDeleteTemplate(activeMerchantId, queryClient);

  const templates = queryClient.getQueryData(['templates', activeMerchantId]);
  const current = templates?.find((t) => t.id === template.id) ?? template;

  const isLoading = upsertMutation.isPending || deleteMutation.isPending;
  const showReset = !current.is_default && !isLoading && !showSuccess;

  function handleSuccess() {
    clearTimeout(timerRef.current);
    if (btnRef.current) btnWidthRef.current = btnRef.current.offsetWidth;
    setShowSuccess(true);
    timerRef.current = setTimeout(() => {
      setShowSuccess(false);
      btnWidthRef.current = undefined;
    }, 3000);
  }

  function handleSave() {
    upsertMutation.mutate({ contact_reason: template.contact_reason, title: template.title, content }, { onSuccess: handleSuccess });
  }

  function handleReset() {
    deleteMutation.mutate(current.id, {
      onSuccess: (defaultTemplate) => {
        setContent(defaultTemplate.content);
        handleSuccess();
      },
    });
  }

  return (
    <>
      <Flex align='center' gap={8} className='h-[62px] mt-[-16px]'>
        <Button type='text' size='large' className='btn-icon btn-dark' icon={<ArrowLeftOutlined />} onClick={onBack} />
        <Flex vertical>
          <h3 className='h3'>{template.contact_reason_label}</h3>
        </Flex>
      </Flex>
      <Flex vertical gap={24} className='overflow-y-auto max-h-[80vh]'>
        <Flex vertical gap={8}>
          <p className='body2'>Titre du modèle</p>
          <p className='body1'>{template.title}</p>
        </Flex>
        <Input.TextArea value={content} onChange={(e) => setContent(e.target.value)} autoSize={{ minRows: 4, maxRows: 12 }} />

        <Flex gap={8} justify='flex-end' align='center'>
          {showSuccess && <p className='body1 text-success-dark'>Vos modifications sont bien enregistrées !</p>}
          {showReset && (
            <Button onClick={handleReset} danger type='link'>
              Réinitialiser
            </Button>
          )}
          <Button
            ref={btnRef}
            type='primary'
            className='btn-success'
            onClick={handleSave}
            loading={isLoading}
            disabled={showSuccess || !content.trim() || content === current.content}
            icon={showSuccess ? <CheckOutlined /> : undefined}
            style={btnWidthRef.current ? { minWidth: btnWidthRef.current } : undefined}
          >
            {!showSuccess && 'Enregistrer les modifications'}
          </Button>
        </Flex>
      </Flex>
    </>
  );
}

export default TemplateEditor;
